import { formatMoney, type Currency } from '@telestore/shared';
import type { ReactNode } from 'react';
import { Button } from '../components/ui/button.js';

/**
 * Shared primitives. Every one of them reads its colours from the design
 * tokens, so nothing here needs to know which template is active (§5).
 */

export function Money({
  amount,
  currency,
  className,
}: {
  amount: number;
  currency: Currency;
  className?: string;
}) {
  return <span className={className}>{formatMoney({ amount, currency })}</span>;
}

export function ComparePrice({ amount, currency }: { amount: number; currency: Currency }) {
  return (
    <span className="text-[0.85em] line-through opacity-55">
      {formatMoney({ amount, currency })}
    </span>
  );
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'accent' | 'warn' }) {
  const styles =
    tone === 'accent'
      ? { background: 'var(--color-accent)', color: 'var(--color-primary-contrast)' }
      : tone === 'warn'
        ? { background: 'var(--color-primary-soft)', color: 'var(--color-text)' }
        : { background: 'var(--color-surface)', color: 'var(--color-text-muted)' };

  return (
    <span
      // `self-start w-fit` so a badge inside a flex column hugs its text
      // instead of stretching into a full-width alert bar.
      className="inline-flex w-fit shrink-0 self-start items-center rounded-full px-2.5 py-1 text-xs font-medium"
      style={styles}
    >
      {children}
    </span>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-10" role="status" aria-live="polite">
      <span
        className="size-5 animate-spin rounded-full border-2 border-current border-t-transparent opacity-60"
        style={{ color: 'var(--color-primary)' }}
      />
      {label ? <span className="text-sm opacity-70">{label}</span> : null}
    </div>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
      <p className="text-base font-semibold">{title}</p>
      {hint ? <p className="max-w-xs text-sm opacity-65">{hint}</p> : null}
      {action}
    </div>
  );
}

export function ErrorNotice({ message, onRetry, retryLabel }: { message: string; onRetry?: () => void; retryLabel: string }) {
  return (
    <div className="mx-4 my-6 rounded-[var(--radius-card)] border p-4" style={{ borderColor: 'var(--color-border)' }}>
      <p className="text-sm">{message}</p>
      {onRetry ? (
        <Button variant="ghost" width="auto" className="mt-3 text-sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Product imagery is the whole proposition in fashion retail, so images get a
 * reserved aspect box (no layout shift), lazy loading, and a neutral
 * placeholder rather than a broken-image icon.
 */
export function ProductImage({
  url,
  alt,
  ratio,
  priority,
}: {
  url: string | null;
  alt: string;
  ratio: string;
  priority?: boolean;
}) {
  return (
    <div
      className="w-full overflow-hidden"
      style={{ aspectRatio: ratio, background: 'var(--color-surface)', borderRadius: 'var(--radius-card)' }}
    >
      {url ? (
        <img
          src={url}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          className="size-full object-cover"
        />
      ) : (
        <div className="size-full" aria-hidden="true" />
      )}
    </div>
  );
}

export function SkeletonCard({ ratio }: { ratio: string }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="skeleton w-full" style={{ aspectRatio: ratio }} />
      <div className="skeleton h-4 w-3/4" />
      <div className="skeleton h-4 w-1/3" />
    </div>
  );
}
